import React from 'react';
import { Radio } from '@mantine/core';

export interface AfaRadioProps {
  /** Si está seleccionado */
  checked?: boolean;
  
  /** Callback cuando cambia el estado */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  
  /** Label del radio */
  label?: string;
  
  /** Descripción adicional */
  description?: string;
  
  /** Mensaje de error */
  error?: string;
  
  /** Si está deshabilitado */
  disabled?: boolean;
  
  /** Tamaño del radio */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  
  /** Radio de bordes */
  radius?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  
  /** Color del radio */
  color?: string;
  
  /** Icono personalizado cuando está marcado */
  icon?: React.FC<any>;
  
  /** Props adicionales para el Radio */
  radioProps?: any;
  
  /** Estilos personalizados */
  styles?: any;
  
  /** Clase CSS */
  className?: string;
  
  /** Valor del radio para formularios */
  value?: string;
  
  /** Nombre del input para formularios */
  name?: string;
  
  /** ID del radio */
  id?: string;
}

/**
 * Wrapper del Radio de Mantine con defaults de AFA y estilos del tema
 * 
 * Ejemplos de uso:
 * 
 * // Básico
 * <AfaRadio
 *   label="Opció 1"
 *   value="option1"
 * />
 * 
 * // Con descripción
 * <AfaRadio
 *   label="Pagament amb targeta"
 *   description="Visa, Mastercard o American Express"
 *   value="card"
 * />
 * 
 * // Grupo de radios
 * <Radio.Group name="payment" label="Mètode de pagament">
 *   <AfaRadio value="card" label="Targeta de crèdit" />
 *   <AfaRadio value="paypal" label="PayPal" />
 *   <AfaRadio value="bank" label="Transferència bancària" />
 * </Radio.Group>
 */
export function AfaRadio({
  checked,
  onChange,
  label,
  description,
  error,
  disabled = false,
  size = 'sm',
  radius = 'lg',
  color,
  icon,
  radioProps,
  styles,
  className,
  value,
  name,
  id,
}: AfaRadioProps) {

  return (
    <Radio
      checked={checked}
      onChange={onChange}
      label={label}
      description={description}
      error={error}
      disabled={disabled}
      size={size}
      radius={radius}
      color={color || 'brand'}
      icon={icon}
      value={value}
      name={name}
      id={id}
      styles={{
        radio: {
          '&:checked': {
            backgroundColor: 'var(--mantine-color-brand-6)',
            borderColor: 'var(--mantine-color-brand-6)',
          },
          '&:focus': {
            borderColor: 'var(--mantine-color-brand-5)',
            boxShadow: `0 0 0 2px var(--mantine-color-brand-1)`,
          },
          borderColor: error ? 'var(--mantine-color-red-6)' : 'var(--mantine-color-brand-3)',
        },
        label: {
          color: 'var(--mantine-other-black)',
          fontWeight: 500,
        },
        description: {
          color: 'var(--mantine-color-neutral-6)',
        },
        error: {
          color: 'var(--mantine-color-red-6)',
        },
        ...styles,
      }}
      {...(className && { className })}
      {...radioProps}
    />
  );
}